// @flow
import type { ChildrenArray } from 'react';
import React from 'react';
import { fallbackLocationData } from '../../validation/runtime-checks/index.js';
import { useValidationErrorsFunctions } from '../../validation/runtime-checks/ValidationContext.js';
import { InternalHeading } from '../../InternalHeading.js';
import { Language, English, Spanish } from '../../languages.js';
import { productColor } from '../../designSystem.js';
import { RSLIntroduction, RSLIdeaSection } from './structural-components.js';

// Id's are in a global namespace, and content authors shouldn't have
// to reason about this. Thus, we prefix the content-author-provided ids
// with a prefix that should prevent id collisions with other html on
// the page.
export function getNamespacedId(contentAuthorProvidedId: string) {
  return `rsl-idea-section__${contentAuthorProvidedId}`;
}

// This component generates navigation for a lesson by interrogating
// the element tree to find all the RSLIdeaSection components and
// extracting the necessary information to link to those sections.
export function InternalNavigationGenerator(props: {|
  // NOTE because this is not a user-facing component it won't get compiler
  // injected props, so we have the explicitly pass this from the parent.
  // This does indicate a bit of clumsiness with this method for switching
  // validation on/off.
  performValidation: boolean | void,
  children: ChildrenArray<
    | React$Element<typeof RSLIntroduction>
    | React$Element<typeof RSLIdeaSection>,
  >,
|}) {
  // CAREFUL: The Lesson will render us before it makes the strong guarantees
  // as to the children order as that happens in an effect.
  const childrenList = React.Children.toArray(props.children);
  const hasIntro = childrenList[0].type.name === RSLIntroduction.name;

  const introductionElement = hasIntro ? childrenList[0] : null;
  const ideaSectionElements = hasIntro ? childrenList.slice(1) : childrenList;

  const refinedIdeaSections: Array<React$Element<typeof RSLIdeaSection>> =
    (childrenList.filter(
      (child) => child.type.name === RSLIdeaSection.name,
    ): any);
  const navigationLinks = refinedIdeaSections.map((el) => ({
    ideaTitle: el.props.title,
    htmlId: getNamespacedId(el.props.id),
  }));

  useIdeaSectionIdUniquenessValidation({
    performValidation: props.performValidation,
    ideaSections: refinedIdeaSections,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 50 }}>
      {introductionElement}
      <InternalIdeasNavigation navigationLinks={navigationLinks} />
      {ideaSectionElements}
    </div>
  );
}

// Validation to ensure uniqueness of ids within the document
function useIdeaSectionIdUniquenessValidation({
  performValidation,
  ideaSections,
}) {
  const { addErrors } = useValidationErrorsFunctions();

  React.useEffect(() => {
    if (!performValidation) return;

    // Build up a map from ids to ideaSections using those ids
    const idToElementsMap: {
      [id: string]: Array<React$Element<typeof RSLIdeaSection>>,
    } = {};
    for (const ideaSection of ideaSections) {
      const { id } = ideaSection.props;
      if (idToElementsMap[id] === undefined) idToElementsMap[id] = [];
      idToElementsMap[id].push(ideaSection);
    }

    // If any of the ids map to multiple ideaSections; add errors for these id props
    const errors = [];
    for (const id of Object.keys(idToElementsMap)) {
      const elements = idToElementsMap[id];
      if (elements.length > 1) {
        errors.push(
          ...elements.map((el) => ({
            message: `Ids must be unique. "${id}" is used ${elements.length} times in this document`,
            ...(el.props._sourceLocations?.id ?? fallbackLocationData),
          })),
        );
      }
    }
    if (errors.length > 0) addErrors(errors);
  });
}

function InternalIdeasNavigation({
  navigationLinks,
}: {|
  navigationLinks: Array<{|
    ideaTitle: string,
    htmlId: string,
  |}>,
|}) {
  return (
    <div>
      <InternalHeading level={2}>
        <Language>
          <English>Ideas</English>
          <Spanish>Ideas</Spanish>
        </Language>
      </InternalHeading>
      <ul
        style={{
          listStyle: 'none',
          paddingLeft: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {navigationLinks.map((link, index) => (
          <InternalIdeaLink key={index} link={link} />
        ))}
      </ul>
    </div>
  );
}

function InternalIdeaLink({ link }) {
  const { isHovered, eventListeners } = useHover();
  return (
    <li>
      <a
        href={`#${link.htmlId}`}
        style={{
          color: productColor.eggplant100,
          fontWeight: 600,
          textDecoration: isHovered ? 'underline' : 'none',
          textUnderlineOffset: 1,
        }}
        onClick={(event) => {
          // Default behaviour will teleport to the section; we instead
          // want to smooth scroll the section into view.
          event.preventDefault();

          // Manually change the #hash for the url since we .preventDefault()ed
          const url = new URL(window.location);
          url.hash = link.htmlId;
          window.history.pushState({}, '', url.toString());

          // NOTE this may not behave nicely in Mathspace as I'm pretty
          // sure the scroll mechanics have been messed up a lot in there.
          window[link.htmlId]?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }}
        {...eventListeners}
      >
        {link.ideaTitle}
      </a>
    </li>
  );
}

function useHover() {
  const [hover, setHover] = React.useState(false);
  const eventListeners = React.useMemo(
    () => ({
      onMouseEnter: () => setHover(true),
      onMouseLeave: () => setHover(false),
    }),
    [],
  );
  return {
    isHovered: hover,
    eventListeners,
  };
}
